// Hideo - categories of Sources and Saved videos (user request, 2/11/2026): what kind of thing an item is (a TV
// programme, a scientific paper, a tutorial…), next to its tags, which are left for the common themes. The fixed
// categories are the same for everyone and cannot be changed (so they never get mixed up with tags); the user can add
// their own (data.categories.src / .saved, per profile), rename and delete them. An item has at most one category
// (source.kind / savedVideo.kind: the fixed id, stored in English and never translated, or the id "ct_<ms>" of an own
// category; '' = none). A new source guesses its category from the link (guessSourceKind).
import { t } from '../i18n'
import type { Category, CategoryGroup, ProfileData } from '../data/types'

/** Longest name of an own category */
export const CATEGORY_NAME_MAX = 30
/** Filter value for "No category" ('' = all) */
export const KIND_NONE = '-'

/** [stored id, English label (shown through t()), Font Awesome icon] */
type FixedKind = readonly [string, string, string]
/** Fixed categories of Sources, in this order */
export const SOURCE_KINDS: readonly FixedKind[] = [
  ['article', 'Article', 'fa-newspaper'],
  ['paper', 'Scientific paper', 'fa-flask'],
  ['book', 'Book', 'fa-book'],
  ['tv', 'TV', 'fa-tv'],
  ['video', 'Video', 'fa-circle-play'],
  ['podcast', 'Podcast', 'fa-podcast'],
  ['website', 'Website', 'fa-globe'],
  ['social', 'Social post', 'fa-hashtag'],
  ['official', 'Official document', 'fa-landmark'],
  ['data', 'Data and statistics', 'fa-chart-pie'],
  ['interview', 'Interview', 'fa-microphone']
]
/** Fixed categories of Saved videos, in this order */
export const SAVED_KINDS: readonly FixedKind[] = [
  ['tutorial', 'Tutorial', 'fa-graduation-cap'],
  ['review', 'Review', 'fa-star-half-stroke'],
  ['vlog', 'Vlog', 'fa-person-walking'],
  ['interview', 'Interview', 'fa-microphone'],
  ['reaction', 'Reaction', 'fa-face-surprise'],
  ['trend', 'Trend or meme', 'fa-fire'],
  ['story', 'Storytelling', 'fa-book-open'],
  ['hook', 'Hook to study', 'fa-anchor'],
  ['editing', 'Editing to study', 'fa-scissors'],
  ['thumbnail', 'Thumbnail to study', 'fa-image']
]
const FIXED: Record<CategoryGroup, readonly FixedKind[]> = { src: SOURCE_KINDS, saved: SAVED_KINDS }
/** Icon of an own category without one */
export const CATEGORY_ICON = 'fa-folder'

/** A category as shown: fixed (label translated) or own */
export interface KindInfo {
  id: string
  name: string
  icon: string
  fixed: boolean
}

/** The profile's own categories of a group */
const ownCategories = (d: Pick<ProfileData, 'categories'>, g: CategoryGroup): Category[] => (d.categories && d.categories[g]) || []
/** Every category of a group: the fixed ones, then the user's own */
export const allKinds = (d: Pick<ProfileData, 'categories'>, g: CategoryGroup): KindInfo[] => [
  ...FIXED[g].map(([id, label, icon]) => ({ id, name: t(label), icon, fixed: true })),
  ...ownCategories(d, g).map(c => ({ id: c.id, name: c.name, icon: c.icon || CATEGORY_ICON, fixed: false }))
]
/** The category with this id (null for '' or an unknown id) */
export const kindInfo = (d: Pick<ProfileData, 'categories'>, g: CategoryGroup, id: string | undefined): KindInfo | null =>
  (id ? allKinds(d, g).find(k => k.id === id) || null : null)
/** A stored category that still exists ('' otherwise: an own category deleted on another device) */
export const isKind = (d: Pick<ProfileData, 'categories'>, g: CategoryGroup, id: unknown): id is string =>
  typeof id === 'string' && !!id && (FIXED[g].some(k => k[0] === id) || ownCategories(d, g).some(c => c.id === id))

/** Items of a category filter: '' = all, KIND_NONE = without a category */
export const ofKind = <T extends { kind?: string }>(list: T[], kind: string): T[] =>
  !kind ? list : kind === KIND_NONE ? list.filter(x => !x.kind) : list.filter(x => x.kind === kind)

// ===== The user's own categories (CategoryDialog.vue). The caller saves =====
const nameTaken = (d: ProfileData, g: CategoryGroup, name: string, except = ''): boolean =>
  allKinds(d, g).some(k => k.id !== except && k.name.toLowerCase() === name.toLowerCase())
/** Adds an own category: its id, or '' for an empty name or a name already used (fixed categories included) */
export function addCategory(d: ProfileData, g: CategoryGroup, name: string, icon = '', now = Date.now()): string {
  name = name.trim().slice(0, CATEGORY_NAME_MAX)
  if (!name || nameTaken(d, g, name)) return ''
  const id = 'ct_' + now
  d.categories[g].push({ id, name, icon })
  return id
}
/** Renames an own category (false: empty, taken, or not an own category) */
export function renameCategory(d: ProfileData, g: CategoryGroup, id: string, name: string): boolean {
  name = name.trim().slice(0, CATEGORY_NAME_MAX)
  const c = ownCategories(d, g).find(x => x.id === id)
  if (!c || !name || nameTaken(d, g, name, id)) return false
  c.name = name
  return true
}
/** Changes the icon of an own category */
export function setCategoryIcon(d: ProfileData, g: CategoryGroup, id: string, icon: string): boolean {
  const c = ownCategories(d, g).find(x => x.id === id)
  if (!c) return false
  c.icon = icon
  return true
}
/** The items that would lose an own category if it were deleted */
export const categoryItems = (d: ProfileData, g: CategoryGroup, id: string): { kind?: string }[] =>
  (g === 'src' ? d.sources : d.savedVideos).filter(x => x.kind === id)
/** Deletes an own category; its items keep no category */
export function deleteCategory(d: ProfileData, g: CategoryGroup, id: string): boolean {
  const before = d.categories[g].length
  d.categories[g] = d.categories[g].filter(c => c.id !== id)
  if (d.categories[g].length === before) return false
  categoryItems(d, g, id).forEach(x => { x.kind = '' })
  return true
}

// ===== A new source's category from its link =====
const SITE_KINDS: readonly (readonly [RegExp, string])[] = [
  [/(^|\.)(arxiv\.org|doi\.org|pubmed\.ncbi\.nlm\.nih\.gov|ncbi\.nlm\.nih\.gov|scholar\.google\.[a-z.]+|researchgate\.net|sciencedirect\.com|springer\.com|link\.springer\.com|nature\.com|science\.org|jstor\.org|semanticscholar\.org|biorxiv\.org|medrxiv\.org|ssrn\.com|frontiersin\.org|plos\.org|mdpi\.com|wiley\.com|onlinelibrary\.wiley\.com|tandfonline\.com|ieee\.org|acm\.org)$/, 'paper'],
  [/(^|\.)(youtube\.com|youtu\.be|vimeo\.com|dailymotion\.com|twitch\.tv)$/, 'video'],
  [/(^|\.)(raiplay\.it|rai\.it|rainews\.it|mediasetinfinity\.mediaset\.it|mediaset\.it|la7\.it|sky\.it|tg24\.sky\.it|bbc\.co\.uk|cnn\.com|discovery\.com)$/, 'tv'],
  [/(^|\.)(open\.spotify\.com|podcasts\.apple\.com|spreaker\.com|podbean\.com|anchor\.fm|raiplaysound\.it)$/, 'podcast'],
  [/(^|\.)(instagram\.com|tiktok\.com|facebook\.com|x\.com|twitter\.com|threads\.net|linkedin\.com|reddit\.com|bsky\.app)$/, 'social'],
  [/(^|\.)(books\.google\.[a-z.]+|goodreads\.com|openlibrary\.org|gutenberg\.org)$/, 'book'],
  [/(^|\.)(istat\.it|eurostat\.ec\.europa\.eu|data\.worldbank\.org|ourworldindata\.org|statista\.com|oecd\.org|data\.gov|dati\.gov\.it)$/, 'data'],
  [/(^|\.)(gov\.it|gazzettaufficiale\.it|europa\.eu|governo\.it|senato\.it|camera\.it|normattiva\.it|who\.int|un\.org)$/, 'official'],
  [/(\.gov|\.gov\.[a-z]{2})$/, 'official']
]
/** Category guessed from a source's link: a known site (papers, video, TV, podcasts, socials, books, data, official
 *  documents), a PDF of a journal-like path, otherwise '' (the user chooses) */
export function guessSourceKind(url: string): string {
  let u: URL
  try { u = new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url) } catch { return '' }
  const host = u.hostname.toLowerCase().replace(/^www\./, '')
  if (!host.includes('.')) return ''
  for (const [re, kind] of SITE_KINDS) if (re.test(host)) return kind
  if (/\/(doi|abs|article|articles|paper|papers)\//i.test(u.pathname) && /\.pdf$/i.test(u.pathname)) return 'paper'
  return ''
}
